// src/features/explore/decorativeCover.ts — deterministic decorative cover pick for
// text-only Explore posts (1.20.50).
//
// Priority chain (see ExploreCard/ExploreMediaPanel): a real image or decorative media
// variant always wins. Only a text-only post reaches this module. The PICK (which
// gradient, which motif) is derived from a stable hash of the post id, so a given post's
// cover never changes between renders or visits, while neighbouring posts vary — and the
// pick itself is theme-INDEPENDENT (a post looks like "the same cover" switching theme,
// just recolored). Mobile's decorativeCover.ts uses the IDENTICAL hash + index math and
// the IDENTICAL gradient table (pinned by decorativeCover.test.ts on both platforms) so
// both platforms agree on the same post.
//
// Gradients stay in the brand's neon family (blue→violet→cyan/magenta) — varied but not
// rainbow slop. Dark theme keeps the near-black-to-neon Signal Glass feel; light theme
// gets a brand-tinted light surface (near-white base -> a deepened brand accent) so the
// tile reads naturally next to the lightened Cityscape/Globe decorative posts. Motifs are
// abstract shapes with NO semantic meaning (owner decision): waves / grid / orbs / rings.

export type ThemeMode = 'light' | 'dark';

/** Three hex stops (start/mid/end of a 135deg gradient), one set per theme mode. */
export interface DecorativeGradient {
  dark: readonly [string, string, string];
  light: readonly [string, string, string];
}

export const DECORATIVE_GRADIENTS: readonly DecorativeGradient[] = [
  { dark: ['#0E141A', '#1B2740', '#4361EE'], light: ['#F5F7FF', '#DCE4FF', '#3A56E0'] },
  { dark: ['#120A1E', '#2B1250', '#9B5DE5'], light: ['#FAF5FF', '#EADCFF', '#8B4FE0'] },
  { dark: ['#0A1620', '#103244', '#4CC9F0'], light: ['#F0FBFF', '#D3F0FC', '#1FA8D9'] },
  { dark: ['#170A1C', '#3A1240', '#F15BB5'], light: ['#FFF5FA', '#FCDCEE', '#E0489E'] },
  { dark: ['#0C1420', '#1E1B45', '#5A189A'], light: ['#F6F5FF', '#E3DCFF', '#4E1489'] },
  { dark: ['#0A1A1C', '#123A3E', '#43C59E'], light: ['#F0FFFA', '#D3F5E7', '#2FA184'] },
] as const;

export const DECORATIVE_MOTIFS = ['waves', 'grid', 'orbs', 'rings'] as const;
export type DecorativeMotif = (typeof DECORATIVE_MOTIFS)[number];

/** The theme-independent pick for a post: which gradient entry, which motif. */
export interface DecorativeCoverPick {
  gradientIndex: number;
  motif: DecorativeMotif;
}

/** Deterministic djb2-family string hash — same algorithm ported verbatim to mobile. */
export function hashPostId(postId: string): number {
  let h = 5381;
  for (let i = 0; i < postId.length; i++) {
    h = (h * 33 + postId.charCodeAt(i)) >>> 0;
  }
  return h;
}

/** Same postId -> same {gradientIndex, motif}, always. Neighbouring ids spread across the set. */
export function pickDecorativeCover(postId: string): DecorativeCoverPick {
  const h = hashPostId(postId);
  const gradientIndex = h % DECORATIVE_GRADIENTS.length;
  const motifIndex = Math.floor(h / DECORATIVE_GRADIENTS.length) % DECORATIVE_MOTIFS.length;
  // Non-null: motifIndex is always in bounds (`h % length`).
  const motif = DECORATIVE_MOTIFS[motifIndex]!;
  return { gradientIndex, motif };
}

/** The three hex stops for `gradientIndex` in the given theme mode. */
export function decorativeGradientStops(
  gradientIndex: number,
  mode: ThemeMode,
): readonly [string, string, string] {
  // Non-null: gradientIndex is always in bounds — callers derive it from pickDecorativeCover.
  const entry = DECORATIVE_GRADIENTS[gradientIndex]!;
  return entry[mode];
}

/** CSS `linear-gradient(...)` for `gradientIndex` in the given theme mode. */
export function decorativeGradientCss(gradientIndex: number, mode: ThemeMode): string {
  const [start, mid, end] = decorativeGradientStops(gradientIndex, mode);
  return `linear-gradient(135deg, ${start} 0%, ${mid} 55%, ${end} 100%)`;
}
